import { RootState } from '@Common/models/redux/store';
import {
  Button,
  Chip,
  ClickAwayListener,
  css,
  Grid,
  IconButton,
  MenuItem,
  Popover,
  styled,
  SvgIcon,
  SvgIconProps,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import {
  DeleteOutlined,
  EditOutlined,
  FileCopyOutlined,
  OpenInNew,
  MoreVertOutlined,
  Cancel,
  FindInPageOutlined,
  LanguageOutlined,
  ImportContactsOutlined,
  MenuBookOutlined,
  SvgIconComponent,
  MoreVert,
  CloseOutlined,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useRouter } from 'next/router';
import { copyRichTextToClip, generateRandomId } from 'core/utils';
import React, { useRef, useState, useEffect, MouseEvent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Clone } from 'src/_common/quillElements/icon';
import { openToastr } from 'src/_common/handlers/services';
import {
  addCitationMarkerAtSelectedCursorPosition,
  higlightCitationRefNodes,
  removeCitationRefNodes,
} from 'src/researcher/component/citationTab/utils';
import {
  setQuillEditorActionType,
  useContextmenuDispatch,
  useContextmenuState,
} from 'src/researcher/context/contextmenu';
import researcherTrack from 'src/researcher/handlers/track';
import { QuillEditorActionTypes } from 'src/researcher/models';
import { pick } from 'src/researcher/utils';
import {
  cursorPointer,
  displayFlex,
  displayNone,
  primaryColor,
  unitPX,
  UNITS,
} from 'src/researcher/styles';
import { useCitationGeneratorDispatch } from '../context';
import { toggleCitationGeneratorEditModal } from '../context/actionCreators';
import {
  CitationData,
  CitationStylesEnum,
  CitationSourceEnum,
  FormattedCitationContent,
} from '../types';
import citationGeneratorTrack from '../handlers/track';
import { TriggeringActions } from '../types/names';
import {
  ignoreWarning,
  addCitationToList,
  removeCitaionToList,
  removeCitedData,
} from '../reducers/actionCreators';
import { copyAllIcon } from '../../../public/images/svgPath.json';
import { CitationElement } from './CitationElement';
import InTextModal from './InTextModal';
import {
  topApaStyleIds,
  getCgBreakpointDown,
  topMlaStyleIds,
  scrollIntoViewCitationCard,
} from '../utils';
import { useEditFormDispatch } from './EditForm/context';
import { initialize } from './EditForm/actionCreators';
import { hasMissingInfo } from './EditForm/config';
import { citationSourceLabels } from '../constants/common.constants';

const hasHttp = (url = '') => /^https?:\/\//i.test(url);

const test = css`
  background-color: red;
`;

const shouldShowModal = (styleId: string) =>
  !topApaStyleIds.includes(styleId) && !topMlaStyleIds.includes(styleId);

interface UseStylesCitationCardProps {
  missingInfo?: boolean;
  pathname?: string;
  expanded?: boolean;
  isLastItem?: boolean;
}
const infoMissingColor = '#F0D99E';

const Root = styled(Grid, {
  shouldForwardProp: (prop) => !(prop === 'isLastItem' || prop === 'missingInfo'),
})<{ missingInfo?: boolean; isLastItem?: boolean }>(
  ({ theme, isLastItem, missingInfo }) => `
    position: relative;
    border-radius: 5px;
    border: 0.5px solid ${theme.palette.primary.main};
    background-color: ${theme.palette.background.paper};
    padding: 9px 4px;
    margin-bottom: ${isLastItem ? unitPX(UNITS['0']) : '12px'};
    transition: background-color 0.5s;
    ${getCgBreakpointDown(theme)} {
      padding-bottom: 0;
      padding-right: 0;
      border-color: ${missingInfo ? infoMissingColor : theme.palette.divider};
      border-width: 1px;
    }`
);
const ActionsContainer = styled(Grid, {
  shouldForwardProp: (prop) => !(prop === 'missingInfo' || prop === 'expanded'),
})<{ missingInfo?: boolean; expanded?: boolean }>(
  ({ theme, expanded, missingInfo }) => `
    position: absolute;
    right: 0px;
    top: 10px;
    z-index: 1;

    ${getCgBreakpointDown(theme)} {
      position: relative;
      top: unset;
      z-index: unset;
      right: unset;
      border-top: 1px solid ${theme.palette.divider};
      padding-top: 4px;
      margin: 0 0 4px -4px;
      display: ${expanded ? 'block' : 'none'};
      height: ${expanded ? 'auto' : '0px'};
      border-color: ${missingInfo ? infoMissingColor : theme.palette.divider};
    }`
);
const ActionToggle = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'missingInfo',
})<{ missingInfo?: boolean }>(
  ({ theme, missingInfo }) => `
    ${getCgBreakpointDown(theme)} {
      display: flex;
      justify-content: center;
      align-items: center;
      border-left: 1px solid ${theme.palette.divider};
      margin-top: -9px;
      border-color: ${missingInfo ? infoMissingColor : theme.palette.divider};
    }`
);

const useStylesCitationCard = makeStyles<Theme>((theme) => ({
  // root: {
  //   position: 'relative',
  //   borderRadius: '5px',
  //   border: `0.5px solid ${theme.palette.primary.main}`,
  //   backgroundColor: theme.palette.background.paper,
  //   padding: '9px 4px',
  //   marginBottom: (props) => (props.isLastItem ? unitPX(UNITS['0']) : '12px'),
  //   transition: 'background-color 0.5s',
  //   [getCgBreakpointDown(theme)]: {
  //     paddingBottom: 0,
  //     paddingRight: 0,
  //     borderColor: (p) => (p.missingInfo ? infoMissingColor : theme.palette.divider),
  //     borderWidth: '1px',
  //   },
  // },
  // citationCardMetaDataArea: {
  //   padding: '0px 10px 0px',
  //   color: '#636E7B',
  //   position: 'relative',
  //   width: '100%',
  //   maxWidth: '100%',
  // },
  citationContent: {
    wordWrap: 'break-word',
    marginTop: 4,
    [getCgBreakpointDown(theme)]: {
      marginTop: 0,
      marginBottom: 4,
    },
  },
  citationContentText: {
    display: 'flex',
    wordBreak: 'break-word',
  },
  citationTextStyle: {
    lineHeight: 1.3,
    '& .headText': {
      fontSize: 13,
      fontWeight: 600,
      marginRight: '4px',
    },
    '& .contentText': {
      fontSize: 12,
      padding: 0,
    },
    '& .contentText, & .headText': {
      color: theme.palette.text.primary,
      [getCgBreakpointDown(theme)]: {
        fontSize: 10,
      },
    },
  },
  urlAndActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '8px',
    [getCgBreakpointDown(theme)]: {
      marginBottom: '4px',
    },
  },
  refNoAndTitleContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingLeft: 6,
    maxWidth: 'calc(100% - 177px)',
    [getCgBreakpointDown(theme)]: {
      maxWidth: '100%',
    },
  },
  iconWrapper: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'column',
    '& > span.iconAltName': {
      display: 'none',
      wordBreak: 'break-word',
      textAlign: 'center',
      fontSize: 9,
      fontWeight: 600,
      maxWidth: 35,
      [getCgBreakpointDown(theme)]: {
        display: 'inline',
      },
    },
  },
  iconButton: {
    padding: 3,
    [getCgBreakpointDown(theme)]: {
      color: theme.palette.primary.main,
    },
  },
  iconStyle: {
    fontSize: 15,
    width: 25,
    height: 22,
    marginRight: 2,
    [getCgBreakpointDown(theme)]: {
      marginRight: 0,
      height: 25,
    },
  },
  // actionButtonContainer: {
  //   position: 'absolute',
  //   right: 0,
  //   top: 10,
  //   zIndex: 1,
  //   [getCgBreakpointDown(theme)]: {
  //     position: 'relative',
  //     top: 'unset',
  //     zIndex: 'unset',
  //     right: 'unset',
  //     borderTop: `1px solid ${theme.palette.divider}`,
  //     paddingTop: 4,
  //     margin: '0 0 4px -4px',
  //     display: (p) => (p.expanded ? 'block' : 'none'),
  //     height: (p) => (p.expanded ? 'auto' : '0px'),
  //     borderColor: (p) => (p.missingInfo ? infoMissingColor : theme.palette.divider),
  //   },
  // },
  inlineTextCitationContainer: {
    width: 'calc(100% + 28px)',
    minHeight: '23px',
    marginRight: '-14px',
    marginLeft: '-14px',
    padding: '0px 14px',
    display: 'flex',
    alignItems: 'center',
    '& $iconStyle': {
      display: 'inline-block',
      opacity: 0,
      position: 'relative',
      top: '3px',
      left: '8px',
      transition: 'opacity 0.2s',
    },
    '@media (hover)': {
      cursor: 'pointer',
      '&:hover': {
        '& $iconStyle': {
          opacity: 1,
        },
      },
    },
    '@media (hover: none)': {
      '& $iconStyle': {
        display: 'none',
      },
    },
    [getCgBreakpointDown(theme)]: {
      marginBottom: '4px',
    },
  },
  copyInTextIcon: {
    height: 15,
    width: 15,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
    [getCgBreakpointDown(theme)]: {
      display: 'none !important',
    },
  },
  menuItem: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  menuText: {
    marginLeft: 6,
  },
  chipDeleteIcon: {
    width: '12px',
    color: '#D6A06E !important',
    '&:hover': {
      color: `${theme.palette.text.secondary} !important`,
    },
    [getCgBreakpointDown(theme)]: {
      width: '15px',
    },
  },
  chipLabel: {
    color: theme.palette.text.primary,
    fontSize: 11,
    paddingLeft: 9,
    marginTop: '-1px',
    [getCgBreakpointDown(theme)]: {
      fontSize: 12,
    },
  },
  chipRoot: {
    height: 15,
    backgroundColor: '#F0D99E',
    marginRight: 4,
    marginTop: 1.5,
    [getCgBreakpointDown(theme)]: {
      height: 17,
    },
  },
  clickableColorSecondary: {
    backgroundColor: '#F0D99E !important',
  },
  citationCardIcon: {
    height: 35,
    width: 35,
    color: '#C2C2C2',
    [getCgBreakpointDown(theme)]: {
      height: 25,
      width: 25,
    },
  },
  actionToggle: {
    display: 'none',
    '> $iconButton': {
      paddingRight: 0,
    },
    // [getCgBreakpointDown(theme)]: {
    //   display: 'flex',
    //   justifyContent: 'center',
    //   alignItems: 'center',
    //   borderLeft: `1px solid ${theme.palette.divider}`,
    //   marginTop: -9,
    //   borderColor: (p) => (p.missingInfo ? infoMissingColor : theme.palette.divider),
    // },
  },
  copyFullSingle: {
    width: 25,
    height: 19,
    marginTop: 2,
    marginRight: undefined,
    [getCgBreakpointDown(theme)]: {
      marginTop: 0,
      height: 25,
    },
  },
}));

const CopyFullSingle = (props: SvgIconProps) => {
  const classes = useStylesCitationCard();

  return (
    <SvgIcon {...props} className={`${props.className || ''} ${classes.copyFullSingle}`}>
      <path fill="inherit" {...copyAllIcon} className={classes.iconStyle} />
    </SvgIcon>
  );
};

type ActionKeys =
  | 'edit'
  | 'delete'
  | 'copyIntext'
  | 'copyFull'
  | 'clone'
  | 'openSource'
  | 'more'
  | 'findOnPage';
type ActionHandlers = {
  [key in ActionKeys]?: (() => void) | ((event: MouseEvent) => void);
};
type CardActionsObj = {
  [action in ActionKeys]: {
    key: ActionKeys;
    // used as icon's tooltip
    name: string;
    // used as text below icon in small screens
    altName?: string;
    icon: SvgIconComponent;
  };
};
const cardActions: CardActionsObj = {
  edit: {
    key: 'edit',
    name: 'Edit',
    icon: EditOutlined,
  },
  delete: {
    key: 'delete',
    name: 'Delete',
    icon: DeleteOutlined,
  },
  copyIntext: {
    key: 'copyIntext',
    name: 'Copy in-text citation',
    altName: 'Copy in-text',
    // icon: FileCopyOutlined,
    icon: CopyFullSingle as SvgIconComponent,
  },
  copyFull: {
    key: 'copyFull',
    name: 'Copy full citation',
    altName: 'Copy full',
    icon: CopyFullSingle as SvgIconComponent,
  },
  clone: {
    key: 'clone',
    name: 'Clone Citation',
    altName: 'Clone',
    icon: Clone as SvgIconComponent,
  },
  openSource: {
    key: 'openSource',
    name: 'Open Citation Source',
    altName: 'Open Source',
    icon: OpenInNew,
  },
  more: {
    key: 'more',
    name: 'More',
    icon: MoreVertOutlined,
  },
  findOnPage: {
    key: 'findOnPage',
    name: 'Find on Page',
    icon: FindInPageOutlined,
  },
};

function CardActions(props: { handlers: ActionHandlers }) {
  const classes = useStylesCitationCard();

  return (
    <Grid container>
      {Object.values(cardActions).map((action) => {
        if (props.handlers[action.key]) {
          return (
            <Grid item key={action.key} className={classes.iconWrapper}>
              <Tooltip title={action.name} placement="top" arrow>
                <IconButton className={classes.iconButton} onClick={props.handlers[action.key]}>
                  <action.icon className={classes.iconStyle} />
                </IconButton>
              </Tooltip>

              <span className="iconAltName">{action.altName || action.name}</span>
            </Grid>
          );
        }

        return null;
      })}
    </Grid>
  );
}

function CollapsedActions({
  onClose,
  anchorEl,
  handlers,
}: {
  onClose: (event: Event) => void;
  anchorEl: HTMLButtonElement | null;
  handlers: ActionHandlers;
}) {
  const classes = useStylesCitationCard();
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      elevation={4}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: [isCowriter && { borderRadius: unitPX(UNITS.pt), py: unitPX(UNITS['0.5pt']) }],
      }}
    >
      {Object.values(cardActions).map((action) => {
        if (handlers[action.key]) {
          return (
            <MenuItem
              key={action.key}
              className={classes.menuItem}
              onClick={handlers[action.key]}
              sx={[isCowriter && { paddingLeft: `${unitPX(UNITS['1.5pt'])}` }]}
            >
              <action.icon style={{ fontSize: 12 }} className={classes.iconStyle} />

              <span className={classes.menuText}>{action.name}</span>
            </MenuItem>
          );
        }

        return null;
      })}
    </Popover>
  );
}

export default function CitationCard(props: {
  citation: CitationData;
  index: number;
  // eslint-disable-next-line react/no-unused-prop-types
  cowriterToolbarAction?: boolean;
  fullCitation: FormattedCitationContent;
  shortCitation: FormattedCitationContent;
  citationNumber?: number;
  onDelete: Function;
  onClone: (oldId: string, newId: string) => void;
  isLastItem?: boolean;
}) {
  const citationStyle = useSelector((state: RootState) => state.ctgr.citationStyle);
  const isSmallScreen = useMediaQuery<Theme>((theme) => getCgBreakpointDown(theme));
  const router = useRouter();
  const { citation, index, isLastItem } = props;
  // const [showIcon, setShowIcon] = useState(false);
  const showIcon = useMemo(
    () => hasMissingInfo(citation, citationStyle),
    [citation, citationStyle]
  );
  const [expanded, setExpanded] = useState(false);
  const classes = useStylesCitationCard();
  const dispatch = useDispatch();
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const editFormDispatch = useEditFormDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [openInText, setOpenInText] = useState(false);

  const fullTextCitationRef = useRef<HTMLDivElement>(null);
  const inTextCitationRef = useRef<HTMLSpanElement>(null);

  const handleOpenInText = (e: React.MouseEvent) => {
    e.stopPropagation();

    const show = shouldShowModal(citationStyle);

    if (show) {
      setOpenInText(true);
      citationGeneratorTrack.userClickedCopyInTextCitationButton(
        citationStyle,
        TriggeringActions.ADDED_CITATIONS
      );
    } else {
      try {
        copyRichTextToClip(
          inTextCitationRef?.current?.innerHTML || '',
          () => openToastr('In-text citation copied to clipboard!'),
          () => openToastr('Failed to copy! Please copy manually!')
        );
      } catch (error) {
        console.log('Error in Copying', error);
      }
    }

    citationGeneratorTrack.userClickedCopyInTextCitationButton(
      citationStyle,
      TriggeringActions.ADDED_CITATIONS
    );
  };

  // useEffect(() => {
  //   setShowIcon(hasMissingInfo(citation));
  // }, [citation]);

  useEffect(() => {
    if (isSmallScreen) {
      scrollIntoViewCitationCard(citation.refId);
    }
  }, [expanded]);

  const actionHandlers: ActionHandlers = {
    copyIntext: handleOpenInText,
    edit: (e) => {
      e.stopPropagation();
      citationGeneratorDispatch(toggleCitationGeneratorEditModal(true, citation, index));
      editFormDispatch(initialize(citation));
      citationGeneratorTrack.userClickedEditCitation(TriggeringActions.ADDED_CITATIONS);
      if (router.pathname.includes('/co-writer')) {
        setAnchorEl(null);
      }
    },
    delete: (e) => {
      e.stopPropagation();
      citationGeneratorTrack.userClickedDeleteCitation(TriggeringActions.DELETE_ONE);
      dispatch(removeCitaionToList(index));
      dispatch(removeCitedData(citation.refId));
      removeCitationRefNodes(citation.refId);
      props.onDelete();
    },
    copyFull: (e) => {
      e.stopPropagation();
      citationGeneratorTrack.userClickedCopySingleFullTextCitationButton(citationStyle);
      try {
        copyRichTextToClip(
          fullTextCitationRef?.current?.innerHTML || '',
          () => openToastr('Full citation copied to clipboard!'),
          () => openToastr('Failed to copy! Please copy manually!')
        );
      } catch (error) {
        console.log('Error in Copying', error);
      }
    },
    more: (e) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget as unknown as HTMLButtonElement);
    },
    clone: (e) => {
      const cloneCitation: CitationData = {
        ...citation,
        refId: generateRandomId(),
        isCloned: true,
        isCited: false,
        id: null, // this is to make sure mongo creates different document for cloned citation
      };
      e.stopPropagation();
      citationGeneratorTrack.userClickedCloneCitation();
      dispatch(addCitationToList(cloneCitation));
      props.onClone(citation.refId, cloneCitation.refId);
      setAnchorEl(null);
    },
    openSource: citation.publicationInfo?.url
      ? (e) => {
          e.stopPropagation();
          citationGeneratorTrack.userClickedOpenSource();
          window.open(
            hasHttp(citation.publicationInfo.url)
              ? citation.publicationInfo.url
              : `https://${citation.publicationInfo.url}`,
            '_blank'
          );
          setAnchorEl(null);
        }
      : undefined,
    findOnPage: router.pathname.includes('/co-writer')
      ? (e) => {
          e.stopPropagation();
          higlightCitationRefNodes(citation.refId, citationStyle);
          researcherTrack.citationCardActions('find_on_page');
          setAnchorEl(null);
        }
      : undefined,
  };

  return (
    <>
      {openInText && (
        <InTextModal
          key={citation.id}
          citation={citation}
          citationStyle={citationStyle}
          onClose={() => setOpenInText(false)}
          shortCitation={props.shortCitation}
          citationNumber={props.citationNumber}
        />
      )}

      <Root
        isLastItem={isLastItem}
        missingInfo={showIcon && !citation.ignore}
        data-citation-card-ref={citation.refId}
        className="showFadeEffect"
        container
        onClick={() => {
          if (isSmallScreen) {
            setExpanded((exp) => !exp);
          }
        }}
      >
        <Grid item direction="column" flex={1} width={isSmallScreen ? 'calc(100% - 32px)' : 0}>
          <Grid
            style={{
              padding: '0px 10px 0px',
              color: '#636E7B',
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
            }}
            item
          >
            <Grid className={classes.urlAndActions}>
              <CitationCardIcon citationSource={citation.citationSource} />

              <Grid item direction="column" zeroMinWidth className={classes.refNoAndTitleContainer}>
                <Typography
                  component="span"
                  noWrap
                  textOverflow="ellipsis"
                  maxWidth="100%"
                  fontSize={13}
                >
                  {citation.publicationInfo?.title}
                </Typography>

                {!isSmallScreen && showIcon && !citation.ignore && (
                  <InfoMissingChip citation={citation} classes={classes} index={index} />
                )}
              </Grid>
            </Grid>

            <Grid container>
              <Grid
                item
                // container
                alignItems="center"
                className={classes.inlineTextCitationContainer}
                onClick={isSmallScreen ? undefined : actionHandlers.copyIntext}
                sx={[
                  router.pathname.includes('/co-writer') && {
                    '&:hover .in-text-copy-btn': { display: 'inline-block' },
                  },
                ]}
              >
                <Typography className={`${classes.citationTextStyle}`} component="span">
                  <span className="headText">In-Text Citation:</span>

                  <span
                    className="contentText"
                    style={{
                      wordBreak: 'break-word',
                    }}
                    ref={inTextCitationRef}
                  >
                    <CitationElement
                      content={props.shortCitation}
                      citationNumber={props.citationNumber}
                    />
                  </span>

                  <Tooltip title="Copy in-text citation" arrow>
                    {router.pathname.includes('/co-writer') ? (
                      <span>
                        <cardActions.copyFull.icon
                          sx={[
                            displayNone,
                            {
                              ml: unitPX(8),
                              width: `${unitPX(14)} !important`,
                              height: `${unitPX(14)} !important`,
                              color: (theme: Theme) => theme.specialColors.ICON_DEFAULT_COLOR,
                              '&:hover': primaryColor,
                            },
                          ]}
                          className="in-text-copy-btn"
                        />
                      </span>
                    ) : (
                      <FileCopyOutlined
                        className={`${classes.iconStyle} ${classes.copyInTextIcon}`}
                        onClick={isSmallScreen ? undefined : actionHandlers.copyIntext}
                      />
                    )}
                  </Tooltip>
                </Typography>

                <Typography
                  className="contentText"
                  ref={fullTextCitationRef}
                  style={{ display: 'none' }}
                  component="span"
                >
                  <CitationElement
                    content={props.fullCitation}
                    citationNumber={props.citationNumber}
                  />
                </Typography>
              </Grid>
            </Grid>

            <Grid item container direction="column" className={classes.citationContent}>
              <Grid
                className={`citationContent__citationContentText ${classes.citationContentText} ${classes.citationTextStyle}`}
              >
                <Grid
                  item
                  container
                  style={{
                    display: 'block',
                  }}
                  onClick={
                    router.pathname.includes('/co-writer') ? actionHandlers.copyFull : undefined
                  }
                  sx={[
                    router.pathname.includes('/co-writer') && {
                      '&:hover .full_text_copy_btn': { display: 'inline-block' },
                      ...cursorPointer,
                    },
                  ]}
                >
                  <Typography className="headText" component="span">
                    Full Citation:
                  </Typography>

                  <Typography className="contentText" component="span">
                    <CitationElement
                      content={props.fullCitation}
                      citationNumber={props.citationNumber}
                    />
                    {router.pathname.includes('/co-writer') && (
                      <>
                        {' '}
                        <Tooltip title="Copy full-text" arrow>
                          <Grid component="span">
                            <cardActions.copyFull.icon
                              sx={[
                                displayNone,
                                {
                                  width: `${unitPX(14)} !important`,
                                  height: `${unitPX(14)} !important`,
                                  color: (theme: Theme) => theme.specialColors.ICON_DEFAULT_COLOR,
                                  '&:hover': primaryColor,
                                },
                              ]}
                              className="full_text_copy_btn"
                            />
                          </Grid>
                        </Tooltip>
                      </>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {isSmallScreen && showIcon && !citation.ignore && (
              <Grid item container mb="8px">
                <InfoMissingChip citation={citation} classes={classes} index={index} />
              </Grid>
            )}
          </Grid>

          {router.pathname.includes('/co-writer') ? (
            <>
              <AddCitationMarkerButton
                citation={citation}
                citationStyle={citationStyle}
                index={index}
              />
              <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                <IconButton
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    position: 'absolute',
                    top: '12px',
                    right: '8px',
                    padding: unitPX(0),
                    width: unitPX(UNITS['2pt']),
                    height: unitPX(UNITS['2pt']),
                  }}
                >
                  <MoreVert sx={{ width: unitPX(UNITS['3pt']), height: unitPX(UNITS['3pt']) }} />
                </IconButton>
              </ClickAwayListener>
            </>
          ) : (
            <ActionsContainer expanded={expanded} missingInfo={showIcon && !citation.ignore}>
              <CardActions
                handlers={
                  isSmallScreen
                    ? pick(actionHandlers, [
                        'edit',
                        'delete',
                        'copyIntext',
                        'copyFull',
                        'clone',
                        'openSource',
                      ])
                    : pick(actionHandlers, ['edit', 'delete', 'copyFull', 'more'])
                }
              />
            </ActionsContainer>
          )}
        </Grid>

        <ActionToggle
          missingInfo={showIcon && !citation.ignore}
          item
          className={classes.actionToggle}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((exp) => !exp);
          }}
        >
          <IconButton className={classes.iconButton}>
            {expanded ? (
              <CloseOutlined className={classes.iconStyle} />
            ) : (
              <MoreVert className={classes.iconStyle} />
            )}
          </IconButton>
        </ActionToggle>
      </Root>

      <CollapsedActions
        anchorEl={anchorEl}
        onClose={(event: Event) => {
          event.stopPropagation();
          setAnchorEl(null);
          console.log('close-citation-actions');
        }}
        handlers={
          router.pathname.includes('/co-writer')
            ? pick(actionHandlers, ['edit', 'delete', 'clone', 'findOnPage', 'openSource'])
            : pick(actionHandlers, ['clone', 'openSource', 'findOnPage'])
        }
      />
    </>
  );
}

const useAddCitationStyle = makeStyles((theme) => ({
  actionIconButton: {
    padding: '3px 9px',
    position: 'absolute',
    top: '8px',
    right: (props: { isCowriter: boolean }) => (props.isCowriter ? '36px' : '8px'),
    '& .citation__actionIcon': {
      width: 20,
      height: 20,
    },
  },
}));

interface AddCitationMarkerButtonComponentProps {
  citation: CitationData;
  citationStyle: CitationStylesEnum | string;
  index: number;
}

function AddCitationMarkerButton({
  citation,
  citationStyle,
  index,
}: AddCitationMarkerButtonComponentProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');
  const contextmenuDispatch = useContextmenuDispatch();
  const markerPostion = useContextmenuState((state) => state.markerPostion);
  const classes = useAddCitationStyle({ isCowriter });
  const citedDataById = useSelector((state: RootState) => state.ctgr.citedDataById);

  return (
    <Tooltip title="Insert Citation" arrow>
      <Button
        aria-label="Insert Citation"
        className={classes.actionIconButton}
        variant="outlined"
        sx={[
          isCowriter && {
            borderRadius: unitPX(UNITS['3pt']),
            borderWidth: unitPX(UNITS['1px']),
            borderColor: primaryColor.color,
            '& .MuiButton-startIcon': {
              marginRight: unitPX(UNITS['0.5pt']),
            },
          },
        ]}
        color="primary"
        startIcon={<AddIcon />}
        onClick={(e) => {
          e.stopPropagation();
          contextmenuDispatch(setQuillEditorActionType(QuillEditorActionTypes.RESET));
          addCitationMarkerAtSelectedCursorPosition(
            markerPostion,
            citation,
            citedDataById[citation.refId]
          );
          dispatch(addCitationToList({ ...citation, isCited: true }, index));
          researcherTrack.citationCardActions('add_citation_marker');
        }}
      >
        Insert
      </Button>
    </Tooltip>
  );
}

function CitationCardIcon({ citationSource }: { citationSource: CitationSourceEnum }) {
  const classes = useStylesCitationCard();

  const Icon: SvgIconComponent = (() => {
    switch (citationSource) {
      case CitationSourceEnum.WEBSITE:
        return LanguageOutlined;
      case CitationSourceEnum.JOURNAL:
        return ImportContactsOutlined;
      case CitationSourceEnum.BOOK:
      default:
        return MenuBookOutlined;
    }
  })();

  return (
    <Tooltip title={citationSourceLabels[citationSource].label} placement="top">
      <Icon className={classes.citationCardIcon} />
    </Tooltip>
  );
}

function InfoMissingChip({
  citation,
  index,
  classes,
}: {
  citation: CitationData;
  index: number;
  classes: ReturnType<typeof useStylesCitationCard>;
}) {
  const dispatch = useDispatch();
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const editFormDispatch = useEditFormDispatch();

  return (
    <Chip
      color="secondary"
      clickable
      onClick={(e) => {
        e.stopPropagation();
        citationGeneratorDispatch(toggleCitationGeneratorEditModal(true, citation, index));
        editFormDispatch(initialize(citation));
        citationGeneratorTrack.userClickedMissingInfoBanner('edit');
      }}
      label={
        <Tooltip title="Edit citation" placement="top" arrow>
          <span>Information missing</span>
        </Tooltip>
      }
      deleteIcon={
        <Tooltip title="Ignore" placement="top" arrow>
          <Cancel />
        </Tooltip>
      }
      onDelete={(e) => {
        e.stopPropagation();
        dispatch(ignoreWarning(index));
        citationGeneratorTrack.userClickedMissingInfoBanner('close');
      }}
      classes={{
        root: classes.chipRoot,
        deleteIcon: classes.chipDeleteIcon,
        label: classes.chipLabel,
        clickableColorSecondary: classes.clickableColorSecondary,
      }}
    />
  );
}
